'use client';

import { useMemo, type ReactElement } from 'react';

import { ContainerPreset } from '@/constants/componentPresets';
import { useViewport } from '@/hooks/dom/use-viewport';
import { CpopoverLayer } from './CpopoverLayer';
import type { CpopoverProps } from './Cpopover.types';
import {
  DEFAULT_BACKDROP_BLUR_PX,
  DEFAULT_BACKDROP_OPACITY,
  DEFAULT_EDGE_MARGIN_PX,
  DEFAULT_ESTIMATED_HEIGHT_PX,
  DEFAULT_GAP_PX,
  DEFAULT_MAX_WIDTH_PX,
  DEFAULT_RADIUS_PX,
  POPOVER_ALIGN,
  POPOVER_PLACEMENT,
} from './constants';
import { getPopoverVariants } from './popover-motion';
import {
  getEffectivePopoverLayout,
  getPopoverIsShown,
  getPopoverMotionStyle,
  getPopoverZIndex,
} from './popover-selectors';
import { usePopoverDismissal } from './usePopoverDismissal';
import { usePopoverModalRegistration } from './usePopoverModalRegistration';
import { usePopoverPosition } from './usePopoverPosition';

export type {
  CpopoverAlign,
  CpopoverPlacement,
  CpopoverProps,
} from './Cpopover.types';

export function Cpopover({
  dataElScope,
  visible,
  anchorElement,
  onClose,
  children,
  placement = POPOVER_PLACEMENT.Auto,
  align = POPOVER_ALIGN.Start,
  gap = DEFAULT_GAP_PX,
  edgeMargin = DEFAULT_EDGE_MARGIN_PX,
  maxWidth = DEFAULT_MAX_WIDTH_PX,
  estimatedHeight = DEFAULT_ESTIMATED_HEIGHT_PX,
  dismissOnOutsideClick = true,
  withBackdrop = false,
  backdropOpacity = DEFAULT_BACKDROP_OPACITY,
  backdropBlur = DEFAULT_BACKDROP_BLUR_PX,
  padding = 0,
  radius = DEFAULT_RADIUS_PX,
  preset = ContainerPreset.HalfGlass,
  boxShadow,
  surfaceStyle,
  zIndexOverride,
  contentWrapperStyle,
  springEnter = false,
  symmetricExit = false,
}: CpopoverProps): ReactElement {
  const { viewport } = useViewport();
  const effectiveLayout = getEffectivePopoverLayout({
    align,
    edgeMargin,
    viewport,
  });
  const { popoverRef, position } = usePopoverPosition({
    align: effectiveLayout.align,
    anchorElement,
    edgeMargin: effectiveLayout.edgeMargin,
    estimatedHeight,
    gap,
    maxWidth,
    placement,
    visible,
  });

  usePopoverModalRegistration({ onClose, visible });
  usePopoverDismissal({
    anchorElement,
    dismissOnOutsideClick,
    onClose,
    popoverRef,
    visible,
  });

  const popoverZIndex = getPopoverZIndex({ zIndexOverride });
  const motionStyle = useMemo(
    () => getPopoverMotionStyle({ popoverZIndex, position }),
    [popoverZIndex, position],
  );
  const variants = useMemo(
    () => getPopoverVariants({ springEnter, symmetricExit }),
    [springEnter, symmetricExit],
  );
  const showPopover = getPopoverIsShown({ visible, position });

  return (
    <CpopoverLayer
      backdropBlur={backdropBlur}
      backdropOpacity={backdropOpacity}
      boxShadow={boxShadow}
      contentWrapperStyle={contentWrapperStyle}
      dataElScope={dataElScope}
      motionStyle={motionStyle}
      onClose={onClose}
      padding={padding}
      popoverRef={popoverRef}
      popoverZIndex={popoverZIndex}
      preset={preset}
      radius={radius}
      showPopover={showPopover}
      surfaceStyle={surfaceStyle}
      variants={variants}
      withBackdrop={withBackdrop}
      zIndexOverride={zIndexOverride}
    >
      {children}
    </CpopoverLayer>
  );
}
